import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { RiskTolerance } from '../../shared/risk-tolerance/RiskTolerance';
import { getAllDocumentNames } from '../documents/store';
import { RiskAssociated, RiskAssociatedValue, SelectedRiskField } from '../risk-tolerance/store';
import { MyDatasetsButtons } from './ClientRiskToleranceButtons';
import { RecalculatingModal } from '../shared/RecalculatingModal';
import { getCurrentRiskField, getCurrentRiskToleranceConfig, getRiskToleranceRecalculating, getSelectedDatasetId, getSelectedRiskField, setCurrentRiskToleranceField, toggleClientRiskField, updateRiskFieldConfig, updateRiskFieldWeighting, getAllRiskToleranceDatasets, getDocumentSpecificHiddenFields, getIsFetchingRiskToleranceDatasets, getRiskToleranceDatasetDefinitions, getSavedHiddenFields, updateRiskFieldFallbackPosition } from './store';
import { UpdatedRiskFieldsModal } from './UpdatedRiskFieldsModal';

export interface ClientDatasetsRiskToleranceProps {
    datasetId: number;
}

export const ClientDatasetsRiskTolerance: React.FC<ClientDatasetsRiskToleranceProps> = ({ datasetId }) => {
    const dispatch = useAppDispatch();
    const clientDatasets = useAppSelector(getAllRiskToleranceDatasets);
    const riskTolerance = useAppSelector(getCurrentRiskToleranceConfig);
    const selectedRiskField = useAppSelector(getCurrentRiskField);
    const selectedField = useAppSelector(getSelectedRiskField);
    const isLoading = useAppSelector(getIsFetchingRiskToleranceDatasets);
    const datasetDefinitions = useAppSelector(getRiskToleranceDatasetDefinitions);
    const hiddenFields = useAppSelector(getSavedHiddenFields);
    const documentHiddenFields = useAppSelector(getDocumentSpecificHiddenFields);
    const allDocumentNames = useAppSelector(getAllDocumentNames);
    const parentDatasetId = useAppSelector(getSelectedDatasetId);
    const isRecalculating = useAppSelector(getRiskToleranceRecalculating);
    const selectRiskField = useCallback((riskField: SelectedRiskField) => dispatch(setCurrentRiskToleranceField(riskField)), [dispatch]);

    const setWeighting = useCallback((weighting: number) => dispatch(updateRiskFieldWeighting(weighting)), [dispatch]);
    const setFallbackPosition = useCallback((fallbackPosition: number) => dispatch(updateRiskFieldFallbackPosition(fallbackPosition)), [dispatch]);
    const updateRiskConfig = useCallback((riskAssociated: RiskAssociated, value: RiskAssociatedValue, selectedCurrency?: string) => dispatch(updateRiskFieldConfig(riskAssociated, value, selectedCurrency)), [dispatch]);
    const toggleRiskField = useCallback((datasetId: number, fieldId: string) => dispatch(toggleClientRiskField(datasetId, fieldId)), [dispatch]);

    const buttonWrapper = useMemo(() => <MyDatasetsButtons />, []);

    if (isRecalculating) {
        return (
            <RecalculatingModal buttons={buttonWrapper} />
        );
    }

    return (
        <>
            <RiskTolerance
                allRiskToleranceDatasets={clientDatasets}
                buttonWrapper={buttonWrapper}
                datasetDefinitions={datasetDefinitions}
                datasetId={datasetId}
                isLoading={isLoading}
                riskTolerance={riskTolerance}
                selectedField={selectedField}
                selectedRiskField={selectedRiskField}
                setRiskToleranceField={selectRiskField}
                setWeighting={setWeighting}
                setFallbackPosition={setFallbackPosition}
                updateRiskConfig={updateRiskConfig}
                hiddenFields={hiddenFields}
                showRiskFieldToggles
                toggleRiskField={toggleRiskField}
                documentHiddenFields={documentHiddenFields}
                allDocumentNames={allDocumentNames}
                agreementTypeDatasetId={parentDatasetId}
            />
            <UpdatedRiskFieldsModal />
        </>
    );
};
